import { useEffect, useState } from 'react'
import { Clock, Plus, Trash2, Mail, ToggleLeft, ToggleRight, Send } from 'lucide-react'
import api from '../../services/api'

type Report = {
  id: string
  name: string
  frequency: 'daily' | 'weekly' | 'monthly'
  recipients: string[]
  enabled: boolean
  last_sent_at: string | null
}

const FREQ_LABELS: Record<string, string> = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }

const blank = (): Omit<Report, 'id' | 'last_sent_at'> => ({
  name: '',
  frequency: 'weekly',
  recipients: [],
  enabled: true,
})

export default function ScheduledReports() {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(blank())
  const [emailInput, setEmailInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/scheduled-reports')
      setReports(data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase()
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return
    if (form.recipients.includes(e)) return
    setForm(f => ({ ...f, recipients: [...f.recipients, e] }))
    setEmailInput('')
  }

  const removeEmail = (e: string) => setForm(f => ({ ...f, recipients: f.recipients.filter(r => r !== e) }))

  const save = async () => {
    if (!form.name.trim() || !form.recipients.length) return
    setSaving(true)
    try {
      await api.post('/scheduled-reports', form)
      setAdding(false)
      setForm(blank())
      setEmailInput('')
      await load()
      showToast('Scheduled report created')
    } catch { showToast('Failed to save') }
    setSaving(false)
  }

  const toggle = async (r: Report) => {
    try {
      await api.patch(`/scheduled-reports/${r.id}`, { enabled: !r.enabled })
      setReports(prev => prev.map(x => x.id === r.id ? { ...x, enabled: !x.enabled } : x))
    } catch {}
  }

  const del = async (id: string) => {
    if (!confirm('Delete this scheduled report?')) return
    try {
      await api.delete(`/scheduled-reports/${id}`)
      setReports(prev => prev.filter(r => r.id !== id))
      showToast('Report deleted')
    } catch {}
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 24px' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: '#1e293b', color: 'white', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 9999 }}>
          {toast}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Clock size={22} color="#6366f1" />
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Scheduled Reports</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Automatic email digests — daily, weekly, or monthly</p>
          </div>
        </div>
        <button
          onClick={() => { setAdding(true); setForm(blank()); setEmailInput('') }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, background: '#6366f1', color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          <Plus size={15} /> New Report
        </button>
      </div>

      {/* New report form */}
      {adding && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 14px', color: '#111827' }}>New Scheduled Report</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Report name</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Weekly Safety Digest"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Frequency</label>
              <select
                value={form.frequency}
                onChange={e => setForm(f => ({ ...f, frequency: e.target.value as any }))}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: 'white', cursor: 'pointer' }}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Recipients</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addEmail()}
                  placeholder="name@company.com.au"
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
                />
                <button onClick={addEmail} style={{ padding: '8px 14px', borderRadius: 8, background: '#e0e7ff', color: '#4338ca', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Add
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {form.recipients.map(e => (
                  <span key={e} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: '#e0e7ff', color: '#4338ca', fontSize: 12, fontWeight: 600 }}>
                    {e}
                    <button onClick={() => removeEmail(e)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6366f1', display: 'flex', alignItems: 'center' }}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              onClick={save}
              disabled={saving || !form.name.trim() || !form.recipients.length}
              style={{ padding: '9px 18px', borderRadius: 8, background: '#6366f1', color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Create Report'}
            </button>
            <button onClick={() => setAdding(false)} style={{ padding: '9px 18px', borderRadius: 8, background: 'white', border: '1px solid #e5e7eb', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Report list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>Loading…</div>
      ) : reports.length === 0 && !adding ? (
        <div style={{ textAlign: 'center', padding: '64px 0', color: '#9ca3af' }}>
          <Send size={32} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.3 }} />
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#374151' }}>No scheduled reports</p>
          <p style={{ margin: '6px 0 0', fontSize: 13 }}>Create one to receive automatic email digests</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map(r => (
            <div key={r.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: r.enabled ? '#111827' : '#9ca3af' }}>{r.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#e0e7ff', color: '#4338ca' }}>
                    {FREQ_LABELS[r.frequency]}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }}>
                  <Mail size={11} />
                  {r.recipients.join(', ')}
                </div>
                {r.last_sent_at && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    Last sent {new Date(r.last_sent_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
              <button onClick={() => toggle(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: r.enabled ? '#6366f1' : '#9ca3af', display: 'flex', alignItems: 'center' }}>
                {r.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
              </button>
              <button onClick={() => del(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
